/**
 * SystemOpsAdmin — Super Admin: System Operations
 *
 * Consolidated operational dashboard with two tabs:
 *   System   — archive job config (system_config table)
 *   Pipeline — pipeline error monitor
 *
 * Tab state is driven by ?tab= search param for deep-linking.
 * Access: super admins only — guarded by SuperAdminLayout.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { formatDateTimeISO } from '../../lib/dateUtils';
import {
  Save, Archive, AlertTriangle, Info, Play,
  RefreshCw, CheckCircle, Trash2, Settings2, GitBranch,
} from 'lucide-react';

// ─── Types ───────────────────────────────────────────────────────────────────

type Tab = 'system' | 'pipeline';

interface ArchiveConfig {
  automatic: boolean;
  schedule_hour: number;
  schedule_minute: number;
  retention_days: number;
  start_date: string | null;
  last_run_at: string | null;
}

interface SystemConfigRow {
  key: string;
  value: ArchiveConfig;
  description: string | null;
  updated_at: string;
  updated_by: string | null;
  updater_name?: string;
}

interface PipelineError {
  id: string;
  source: 'recording' | 'processing_job' | 'session';
  sessionId: string;
  errorMessage: string;
  detail: string;
  timestamp: string;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const HOURS   = Array.from({ length: 24 }, (_, i) => i);
const MINUTES = [0, 15, 30, 45];
const pad     = (n: number) => String(n).padStart(2, '0');

// ─── System tab ──────────────────────────────────────────────────────────────

function SystemTab() {
  const { user } = useAuth();

  const [config, setConfig] = useState<SystemConfigRow | null>(null);
  const [form, setForm] = useState<ArchiveConfig>({
    automatic: false,
    schedule_hour: 3,
    schedule_minute: 0,
    retention_days: 7,
    start_date: null,
    last_run_at: null,
  });
  const [loading,          setLoading]          = useState(true);
  const [saving,           setSaving]           = useState(false);
  const [triggering,       setTriggering]       = useState(false);
  const [error,            setError]            = useState<string | null>(null);
  const [successMsg,       setSuccessMsg]       = useState<string | null>(null);
  const [startDateWarning, setStartDateWarning] = useState<string | null>(null);

  const loadConfig = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabase
        .from('system_config')
        .select(`
          key,
          value,
          description,
          updated_at,
          updated_by,
          updater:updated_by ( full_name )
        `)
        .eq('key', 'archive_recordings')
        .single();

      if (fetchError) throw fetchError;

      const updaterName = (data as any).updater?.full_name;
      setConfig({ ...data, updater_name: updaterName });
      setForm(data.value as ArchiveConfig);
    } catch (err: any) {
      setError(err.message ?? 'Failed to load configuration');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  const handleStartDateChange = (val: string) => {
    setStartDateWarning(null);
    if (val && new Date(val) > new Date()) {
      setStartDateWarning('Start date is in the future — the job will not archive any recordings until that date is reached.');
    }
    setForm(prev => ({ ...prev, start_date: val || null }));
  };

  const handleSave = async () => {
    setError(null);
    setSuccessMsg(null);
    if (form.retention_days < 1) { setError('Retention days must be at least 1.'); return; }
    setSaving(true);
    try {
      const { error: upsertError } = await supabase
        .from('system_config')
        .upsert({
          key:        'archive_recordings',
          value:      { ...form, last_run_at: config?.value.last_run_at ?? null },
          updated_by: user?.id ?? null,
          updated_at: new Date().toISOString(),
        });
      if (upsertError) throw upsertError;
      setSuccessMsg('Configuration saved. Changes take effect on the next 15-minute Inngest tick.');
      await loadConfig();
    } catch (err: any) {
      setError(err.message ?? 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleRunNow = async () => {
    setError(null);
    setSuccessMsg(null);
    setTriggering(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error('Not authenticated');

      const res = await fetch('/api/admin/trigger-archive', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.error ?? 'Trigger failed');
      setSuccessMsg(body.message);
      await loadConfig();
    } catch (err: any) {
      setError(err.message ?? 'Failed to trigger archive job');
    } finally {
      setTriggering(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-orca-elevate" />
        <p className="ml-3 text-steel">Loading configuration…</p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl space-y-6">

      {error && (
        <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" /><span>{error}</span>
        </div>
      )}
      {successMsg && (
        <div className="flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
          <Info className="w-5 h-5 flex-shrink-0 mt-0.5" /><span>{successMsg}</span>
        </div>
      )}

      {/* ── Archive Recordings card ─────────────────────────────────────────── */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">

        {/* Card header */}
        <div className="flex items-center gap-3 px-6 py-4 border-b border-steel/10 bg-ice/40">
          <Archive className="w-5 h-5 text-orca-elevate" />
          <div>
            <h3 className="text-base font-semibold text-navy">Archive Recordings</h3>
            <p className="text-xs text-steel mt-0.5">
              {config?.description ?? 'Session recording archive job configuration'}
            </p>
          </div>
        </div>

        <div className="px-6 py-6 space-y-6">

          {/* Automatic toggle */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-navy">Automatic archiving</p>
              <p className="text-xs text-steel mt-0.5">
                When enabled, the job runs daily at the configured schedule.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setForm(prev => ({ ...prev, automatic: !prev.automatic }))}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-orca-elevate focus:ring-offset-2 ${
                form.automatic ? 'bg-orca-elevate' : 'bg-steel/30'
              }`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                form.automatic ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
          </div>

          {/* S3 credentials notice */}
          {form.automatic && (
            <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-md text-xs text-blue-700">
              <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>
                Requires <code className="font-mono bg-blue-100 px-1 rounded">ARCHIVE_S3_ENDPOINT</code>,{' '}
                <code className="font-mono bg-blue-100 px-1 rounded">ARCHIVE_S3_ACCESS_KEY</code>,{' '}
                <code className="font-mono bg-blue-100 px-1 rounded">ARCHIVE_S3_SECRET_KEY</code>, and{' '}
                <code className="font-mono bg-blue-100 px-1 rounded">ARCHIVE_S3_BUCKET</code>{' '}
                to be set in Coolify → Service → Environment Variables.
              </span>
            </div>
          )}

          {/* Schedule */}
          <div>
            <label className="block text-sm font-medium text-navy mb-2">
              Schedule <span className="text-steel font-normal">(UTC)</span>
            </label>
            <div className="flex items-center gap-3">
              <div>
                <label className="block text-xs text-steel mb-1">Hour</label>
                <select
                  value={form.schedule_hour}
                  onChange={e => setForm(prev => ({ ...prev, schedule_hour: Number(e.target.value) }))}
                  className="w-24 px-3 py-2 bg-white border border-steel/30 rounded-md text-navy text-sm focus:outline-none focus:ring-2 focus:ring-orca-elevate"
                >
                  {HOURS.map(h => <option key={h} value={h}>{pad(h)}:00</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-steel mb-1">Minute</label>
                <select
                  value={form.schedule_minute}
                  onChange={e => setForm(prev => ({ ...prev, schedule_minute: Number(e.target.value) }))}
                  className="w-24 px-3 py-2 bg-white border border-steel/30 rounded-md text-navy text-sm focus:outline-none focus:ring-2 focus:ring-orca-elevate"
                >
                  {MINUTES.map(m => <option key={m} value={m}>{pad(m)}</option>)}
                </select>
              </div>
              <div className="mt-4">
                <span className="text-sm text-steel">
                  → runs daily at{' '}
                  <strong className="text-navy">{pad(form.schedule_hour)}:{pad(form.schedule_minute)} UTC</strong>
                </span>
              </div>
            </div>
          </div>

          {/* Retention days */}
          <div>
            <label className="block text-sm font-medium text-navy mb-1">
              Retention period <span className="text-steel font-normal">(days)</span>
            </label>
            <p className="text-xs text-steel mb-2">
              Recordings older than this many days will be archived. Minimum 1.
            </p>
            <input
              type="number"
              min={1}
              value={form.retention_days}
              onChange={e => setForm(prev => ({ ...prev, retention_days: Math.max(1, Number(e.target.value)) }))}
              className="w-32 px-3 py-2 bg-white border border-steel/30 rounded-md text-navy text-sm focus:outline-none focus:ring-2 focus:ring-orca-elevate"
            />
          </div>

          {/* Start date */}
          <div>
            <label className="block text-sm font-medium text-navy mb-1">
              Start date <span className="text-steel font-normal">(optional)</span>
            </label>
            <p className="text-xs text-steel mb-2">
              Only archive recordings created on or after this date. Leave empty to apply to all recordings.
              The retention period is always applied regardless of this setting.
            </p>
            <input
              type="date"
              value={form.start_date ?? ''}
              onChange={e => handleStartDateChange(e.target.value)}
              className="w-48 px-3 py-2 bg-white border border-steel/30 rounded-md text-navy text-sm focus:outline-none focus:ring-2 focus:ring-orca-elevate"
            />
            {form.start_date && (
              <button
                type="button"
                onClick={() => { setForm(prev => ({ ...prev, start_date: null })); setStartDateWarning(null); }}
                className="ml-2 text-xs text-steel hover:text-navy underline"
              >
                Clear
              </button>
            )}
            {startDateWarning && (
              <p className="mt-1 text-xs text-amber-600 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> {startDateWarning}
              </p>
            )}
          </div>

          {/* Last run */}
          {config?.value.last_run_at && (
            <div className="pt-2 border-t border-steel/10 text-xs text-steel">
              Last run: <strong className="text-navy">{formatDateTimeISO(config.value.last_run_at)}</strong>
            </div>
          )}

        </div>

        {/* Card footer */}
        <div className="flex items-center justify-between px-6 py-4 bg-ice/40 border-t border-steel/10">
          <div className="text-xs text-steel">
            {config?.updater_name
              ? <>Updated by <strong className="text-navy">{config.updater_name}</strong> on {formatDateTimeISO(config.updated_at)}</>
              : config?.updated_at
              ? <>Last updated {formatDateTimeISO(config.updated_at)}</>
              : 'Not yet saved'}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleRunNow}
              disabled={triggering || saving}
              title="Trigger an immediate archive run, bypassing the automatic and schedule settings. Retention days and start date still apply."
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-orca-elevate border border-orca-elevate hover:bg-orca-elevate/5 rounded-md transition-colors disabled:opacity-50"
            >
              {triggering
                ? <><div className="w-4 h-4 border-2 border-orca-elevate/40 border-t-orca-elevate rounded-full animate-spin" /> Running…</>
                : <><Play className="w-4 h-4" /> Run now</>}
            </button>
            <button
              onClick={handleSave}
              disabled={saving || triggering}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-orca-elevate hover:bg-orca-elevate/90 rounded-md transition-colors disabled:opacity-50"
            >
              {saving
                ? <><div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> Saving…</>
                : <><Save className="w-4 h-4" /> Save changes</>}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}

// ─── Pipeline tab ─────────────────────────────────────────────────────────────

function PipelineTab() {
  const [errors,      setErrors]      = useState<PipelineError[]>([]);
  const [isLoading,   setIsLoading]   = useState(true);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  const fetchErrors = useCallback(async () => {
    setIsLoading(true);
    const results: PipelineError[] = [];

    const { data: recordings } = await supabase
      .from('session_recordings')
      .select('id, session_id, phase, status, error_message, created_at')
      .eq('status', 'error')
      .order('created_at', { ascending: false })
      .limit(50);

    for (const r of recordings || []) {
      results.push({
        id:           `rec-${r.id}`,
        source:       'recording',
        sessionId:    r.session_id,
        errorMessage: r.error_message || 'Unknown recording error',
        detail:       `Phase: ${r.phase}`,
        timestamp:    r.created_at,
      });
    }

    const { data: jobs } = await supabase
      .from('processing_jobs')
      .select('id, session_id, job_type, status, error_message, created_at')
      .eq('status', 'failed')
      .order('created_at', { ascending: false })
      .limit(50);

    for (const j of jobs || []) {
      results.push({
        id:           `job-${j.id}`,
        source:       'processing_job',
        sessionId:    j.session_id,
        errorMessage: j.error_message || 'Unknown job error',
        detail:       `Job type: ${j.job_type}`,
        timestamp:    j.created_at,
      });
    }

    const { data: sessions } = await supabase
      .from('sessions')
      .select('id, status, processing_error, updated_at')
      .eq('status', 'failed')
      .order('updated_at', { ascending: false })
      .limit(50);

    for (const s of sessions || []) {
      results.push({
        id:           `sess-${s.id}`,
        source:       'session',
        sessionId:    s.id,
        errorMessage: (s as Record<string, unknown>).processing_error as string || 'Session marked as failed',
        detail:       'Session status: failed',
        timestamp:    s.updated_at,
      });
    }

    results.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    setErrors(results);
    setIsLoading(false);
    setLastRefresh(new Date());
  }, []);

  useEffect(() => {
    fetchErrors();
    const interval = setInterval(fetchErrors, 30000);
    return () => clearInterval(interval);
  }, [fetchErrors]);

  const handleDismiss = async (err: PipelineError) => {
    if (err.source === 'recording') {
      await supabase.from('session_recordings')
        .update({ status: 'dismissed', error_message: null })
        .eq('id', err.id.replace('rec-', ''));
    } else if (err.source === 'processing_job') {
      await supabase.from('processing_jobs')
        .update({ status: 'dismissed' })
        .eq('id', err.id.replace('job-', ''));
    } else {
      await supabase.from('sessions')
        .update({ status: 'draft' })
        .eq('id', err.id.replace('sess-', ''));
    }
    fetchErrors();
  };

  const formatTimestamp = (ts: string) =>
    new Date(ts).toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });

  const sourceLabel = (s: PipelineError['source']) =>
    s === 'recording' ? 'Recording' : s === 'processing_job' ? 'Processing Job' : 'Session';

  const sourceBadgeColor = (s: PipelineError['source']) =>
    s === 'recording'      ? 'bg-red-100 text-red-700'
    : s === 'processing_job' ? 'bg-orange-100 text-orange-700'
    : 'bg-yellow-100 text-yellow-700';

  return (
    <div className="space-y-6">

      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-steel">
          Auto-refreshes every 30s · Last refresh: {lastRefresh.toLocaleTimeString()}
        </p>
        <button
          onClick={fetchErrors}
          disabled={isLoading}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-steel/30 rounded-lg hover:bg-gray-50 text-navy font-medium text-sm disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Summary badge */}
      <div className="flex gap-4">
        <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
          errors.length > 0 ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
        }`}>
          {errors.length > 0 ? <AlertTriangle className="h-5 w-5" /> : <CheckCircle className="h-5 w-5" />}
          <span className="font-semibold">
            {errors.length > 0
              ? `${errors.length} active error${errors.length !== 1 ? 's' : ''}`
              : 'No errors'}
          </span>
        </div>
      </div>

      {/* Error list */}
      {errors.length === 0 ? (
        <div className="text-center py-16">
          <CheckCircle className="h-12 w-12 text-green-400 mx-auto mb-4" />
          <p className="text-lg text-steel font-medium">All pipelines healthy</p>
          <p className="text-sm text-steel/60 mt-1">No failed recordings, jobs, or sessions</p>
        </div>
      ) : (
        <div className="space-y-3">
          {errors.map(err => (
            <div key={err.id} className="bg-white border border-steel/20 rounded-lg p-4 shadow-sm">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${sourceBadgeColor(err.source)}`}>
                      {sourceLabel(err.source)}
                    </span>
                    <span className="text-xs text-steel">{formatTimestamp(err.timestamp)}</span>
                  </div>
                  <p className="text-sm font-mono text-navy/80 break-all">{err.errorMessage}</p>
                  <div className="flex gap-4 mt-2 text-xs text-steel">
                    <span>Session: <code className="text-navy">{err.sessionId.slice(0, 8)}…</code></span>
                    <span>{err.detail}</span>
                  </div>
                </div>
                <button
                  onClick={() => handleDismiss(err)}
                  title="Dismiss error"
                  className="shrink-0 p-1.5 text-steel hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Page shell ───────────────────────────────────────────────────────────────

const TABS: { id: Tab; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { id: 'system',   label: 'System',   icon: Settings2 },
  { id: 'pipeline', label: 'Pipeline', icon: GitBranch },
];

export default function SystemOpsAdmin() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = (searchParams.get('tab') as Tab | null) ?? 'system';

  const setTab = (t: Tab) => setSearchParams({ tab: t }, { replace: true });

  return (
    <div className="px-6 py-8 space-y-6">

      {/* Page header */}
      <div>
        <h2 className="text-2xl font-semibold text-navy mb-1">System Operations</h2>
        <p className="text-steel text-sm">
          Infrastructure configuration and pipeline monitoring.
        </p>
      </div>

      {/* Tab bar */}
      <div className="border-b border-steel/20">
        <nav className="flex gap-1" aria-label="System ops tabs">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
                tab === id
                  ? 'border-orca-elevate text-orca-elevate'
                  : 'border-transparent text-steel hover:text-navy hover:border-steel/30'
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      {tab === 'system'   && <SystemTab />}
      {tab === 'pipeline' && <PipelineTab />}

    </div>
  );
}
